// The lead's numbers on the dialer (owner request, PJ 2026-10-23: "I want to prioritize owner cells over everything,
// because that is the direct contact to the decision maker. And if they don't pick up, or if we choose to call the store,
// that should be an option." / "For the ones where it's one owner in several stores, just mark this is the owner cell and
// he owns several stores. If we had called that owner cell before, just have like some sort of thing, you have called
// this before."). Pure: the lead card's number lines, the "Call the store" button, the called-before and multi-store
// lines, and the wrap-up's "Call the store now" / "Call the owner now" (tests/fd-owner-line.test.ts).
//
// Dial / Space call the default number (QueueItem.numbers.defaultTarget: the owner's when there is one); A ("Call the
// store", S is Skip) dials the store line. In the wrap-up after an owner's-line call, A saves the outcome (No answer
// with "Owner didn't answer; calling the store" when none was picked) and dials the store at once; after a store-line
// call, the same for the owner.
import type { DispositionCode, GateResult, QueueItem } from '../../../src/shared/api';
import type { CalledBefore, DialTarget, QueueItemNumber, QueueItemNumbers } from '../../../src/shared/api-numbers';
import { NUMBER_ROLE_WORDS, numberLabel, otherTarget } from '../../../src/shared/api-numbers';
import { DISPOSITION_META, buttonLabel, fmtPhone, outcomeButtonFor } from './labels';

/** The outcomes after which the other line may be called right away: nobody (or not the owner) picked up, or the number
 *  was wrong. The rest close, rest or schedule the lead (a meeting, "not interested", a callback, DNC...). */
export const OTHER_LINE_OUTCOMES: readonly DispositionCode[] = ['no_answer', 'vm_live', 'gatekeeper_intel', 'gatekeeper_blocked', 'wrong_number'];

/** The note the wrap-up's "Call the store now" / "Call the owner now" logs when no outcome was picked. */
export function otherLineNote(called: DialTarget): string {
  return called === 'owner' ? "Owner didn't answer; calling the store" : "Store didn't answer; calling the owner";
}

/** "Call the store now" after an owner's-line call, "Call the owner now" after a store-line call. */
export function otherLineLabel(called: DialTarget): string {
  return called === 'owner' ? 'Call the store now' : 'Call the owner now';
}

/** The item's numbers block, or null (an older server, or a lead with one number and no role). */
export function itemNumbers(item: QueueItem | null | undefined): QueueItemNumbers | null {
  return item?.numbers ?? null;
}

/** One of the lead's numbers by role (null when it has none). */
export function numberOf(item: QueueItem | null | undefined, target: DialTarget): QueueItemNumber | null {
  const n = itemNumbers(item);
  return n ? (target === 'owner' ? n.owner : n.store) : null;
}

/** The dial gate for calling `target` now: that number's own gate; the default (no target) is QueueItem.dialGate. */
export function gateFor(item: QueueItem, target?: DialTarget | null): GateResult {
  if (!target || itemNumbers(item)?.defaultTarget === target) return item.dialGate;
  return numberOf(item, target)?.gate ?? item.dialGate;
}

/** The number Dial calls for `target` (the default when absent): its E.164. */
export function e164For(item: QueueItem, target?: DialTarget | null): string {
  const n = target ? numberOf(item, target) : null;
  if (n) return n.e164;
  const d = itemNumbers(item)?.defaultTarget;
  return (d ? numberOf(item, d)?.e164 : null) ?? item.business.phoneE164;
}

/** Which of the lead's numbers `e164` is (a call's to_e164): its role, or null (the lead's one number, or unknown). */
export function targetOfNumber(item: QueueItem | null | undefined, e164: string | null | undefined): DialTarget | null {
  const n = itemNumbers(item);
  if (!n || !e164) return null;
  if (n.owner?.e164 === e164) return 'owner';
  if (n.store?.e164 === e164) return 'store';
  return null;
}

/** The dial pad's number (manualDial.ts): the number the pad found the lead by (`dialE164`, the number typed; an older
 *  server: its own phone_e164). When that is not the lead's default (its store line, or, review 2026-10-23, the owner's
 *  line on a lead whose default is the store), the pad dials it by its role; else the lead's default (null). */
export function padTarget(item: QueueItem, dialE164?: string | null): DialTarget | null {
  const t = targetOfNumber(item, dialE164 || item.business.phoneE164);
  return t && t !== item.numbers?.defaultTarget ? t : null;
}

/** The store line the "Call the store (A)" button dials on the lead card: shown only when Dial calls the owner and the
 *  lead has a store line too. Nothing new shows when the default is the store line (no owner number). */
export function storeButton(item: QueueItem | null | undefined): QueueItemNumber | null {
  const n = itemNumbers(item);
  return n && n.defaultTarget === 'owner' && n.store ? n.store : null;
}

/** The lead card's line for a number: "Owner's line (likely cell): (604) 555-0100", "Store line: (604) 555-0199". */
export function numberLine(n: Pick<QueueItemNumber, 'target' | 'likelyCell' | 'e164'>): string {
  return `${numberLabel(n)}: ${fmtPhone(n.e164)}`;
}

/** "Oct 21" in the setter's own time. */
export function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
}

/** "You've called this number before: 2 calls, last Oct 21 by Max (No answer)" (every lead and campaign). */
export function calledBeforeLine(cb: CalledBefore | null | undefined, fmtDate: (iso: string) => string = shortDate): string | null {
  if (!cb || !(cb.calls > 0)) return null;
  const outcome = cb.lastOutcome ? DISPOSITION_META[cb.lastOutcome]?.label ?? cb.lastOutcome : 'no outcome logged';
  return `You've called this number before: ${cb.calls} call${cb.calls === 1 ? '' : 's'}, last ${fmtDate(cb.lastAt)}${cb.lastBy ? ` by ${cb.lastBy}` : ''} (${outcome})`;
}

/** "This owner has 3 stores in our lists: Green Leaf (Corona), Green Leaf (Riverside) and Green Leaf (Norco)". Names and
 *  cities only; this lead first; "and N more" past the names the server sent. Review 2026-10-23: a group the licence list
 *  made (basis 'licence_group': different numbers, "same owner?" in the list) reads "This owner may have 3 stores (the
 *  licence list groups them; 2 in our lists): ...", or, with only this one in our lists, "...; this is the only one in our
 *  lists". */
export function ownerStoresLine(os: QueueItemNumbers['ownerStores'] | null | undefined): string | null {
  if (!os || !os.stores.length) return null;
  const names = os.stores.map((s) => (s.city ? `${s.name} (${s.city})` : s.name));
  const more = os.total - names.length;
  const list = more > 0 ? `${names.join(', ')} and ${more} more` : names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  if (os.basis === 'licence_group') {
    const listed = Math.max(os.listed ?? os.total, os.total);
    if (listed < 2) return null;
    const head = `This owner may have ${listed} stores (the licence list groups them`;
    if (os.total < 2) return `${head}); this is the only one in our lists`;
    return `${head}${os.total < listed ? `; ${os.total} in our lists` : ''}): ${list}`;
  }
  if (os.total < 2) return null;
  return `This owner has ${os.total} stores in our lists: ${list}`;
}

/** A call's number in the call bar and history: "Owner's line" / "Store line", or null. */
export function roleWords(role: DialTarget | null | undefined): string | null {
  return role ? NUMBER_ROLE_WORDS[role] : null;
}

/**
 * The wrap-up's "Call the store now" / "Call the owner now" button for a call to `called`, or null when not shown (the
 * call went to the lead's one number, or the lead has no other number). why null = ready.
 */
export function otherLineButton(args: {
  item: QueueItem | null; called: DialTarget | null; code: DispositionCode | null; stored: DispositionCode | null;
  saving: boolean; dropping: boolean;
}): { target: DialTarget; label: string; why: string | null } | null {
  if (!args.item || !args.called) return null;
  const target = otherTarget(args.called);
  const n = numberOf(args.item, target);
  if (!n) return null;
  const label = otherLineLabel(args.called);
  const code = args.stored ?? args.code;
  if (code && !OTHER_LINE_OUTCOMES.includes(code)) {
    return { target, label, why: `${label} is for a missed call, a voicemail, a gatekeeper or a wrong number, not "${buttonLabel(outcomeButtonFor(code) ?? code)}".` };
  }
  if (args.saving) return { target, label, why: 'Saving…' };
  if (args.dropping) return { target, label, why: 'Still confirming the voicemail drop.' };
  return { target, label, why: null };
}

/** The number the call bar shows: the one the call on screen went to (or is going to), else what Dial calls; with its
 *  role ("Owner's line (likely cell)" before the call, "Owner's line" / "Store line" for a call). */
export function callBarNumber(item: QueueItem, to: string | null): { e164: string; label: string | null; ext: string | null } {
  if (to) {
    const t = targetOfNumber(item, to);
    return { e164: to, label: roleWords(t), ext: to === item.business.phoneE164 ? item.business.phoneExt : null };
  }
  const d = item.numbers?.defaultTarget ?? null;
  const n = d ? numberOf(item, d) : null;
  const e164 = e164For(item);
  return { e164, label: n ? numberLabel(n) : null, ext: e164 === item.business.phoneE164 ? item.business.phoneExt : null };
}
